// src/pages/VentingRoomDashboard.js
import React, { useState, useEffect, useContext } from 'react';
import { Route, Routes, useNavigate, useLocation, Link } from 'react-router-dom';
import {
    FaHome,
    FaTasks,
    FaChartLine,
    FaCog,
    FaBell,
    FaSpinner
} from 'react-icons/fa';
import '../styles/VentingRoomDashboard.css';
import { UserContext } from '../UserContext';

// Import the MSAL instance and login request configuration
import { msalInstance, loginRequest } from '../msalConfig';

// Import content pages
import DashboardContent from './VentingRoomContent/DashboardContent';
import UsageReportContent from './VentingRoomContent/UsageReportContent';
import TrendingNewsContent from './VentingRoomContent/TrendingNewsContent';
import PrivacySettingsContent from './VentingRoomContent/PrivacySettingsContent';
import NotificationsContent from './VentingRoomContent/NotificationsContent';

const API_BASE = "";

function VentingRoomDashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    // Assuming UserContext provides a boolean 'isAuthenticated'
    const { isAuthenticated } = useContext(UserContext); 
    const [activeSidebarItem, setActiveSidebarItem] = useState('dashboard');
    const [dashboardData, setDashboardData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // This useEffect sets the active sidebar item based on the URL
    useEffect(() => {
        const pathSegments = location.pathname.split('/');
        const currentPathSegment = pathSegments[pathSegments.length - 1];

        if (currentPathSegment === 'venting-room-dashboard' || currentPathSegment === 'dev-venting-room-dashboard' || currentPathSegment === '') {
            setActiveSidebarItem('dashboard');
        } else {
            setActiveSidebarItem(currentPathSegment);
        }
    }, [location.pathname]);

    // This useEffect fetches data from the backend based on the active view
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setError(null);

            let endpoint = '';
            switch (activeSidebarItem) {
                case 'dashboard':
                    endpoint = `${API_BASE}/api/venting-room/dashboard-content`;
                    break;
                case 'usage-report':
                    endpoint = `${API_BASE}/api/venting-room/usage-report`;
                    break;
                case 'trending-news':
                    endpoint = `${API_BASE}/api/venting-room/trending-news`;
                    break;
                case 'privacy-settings':
                    endpoint = `${API_BASE}/api/venting-room/privacy-settings`;
                    break;
                case 'notifications':
                    endpoint = `${API_BASE}/api/venting-room/notifications`;
                    break;
                default:
                    setIsLoading(false);
                    return;
            }

            let accessToken = null;
            try {
                // Attempt to get a token silently. This is the preferred method.
                // It will use a cached token if available and valid.
                const accessTokenResponse = await msalInstance.acquireTokenSilent(loginRequest);
                accessToken = accessTokenResponse.accessToken;
            } catch (err) {
                console.warn("Silent token acquisition failed. Attempting with redirect/popup.", err);
                // If silent acquisition fails (e.g., token expired), attempt a new login.
                // For a production app, you would likely trigger a login flow here.
                setError("Your session has expired or is invalid. Please log in again.");
                setIsLoading(false);
                return;
            }

            try {
                // Now, make the API call with the acquired token in the Authorization header
                const response = await fetch(endpoint, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`, // Pass the access token
                        'Content-Type': 'application/json',
                    },
                });

                console.log("API Response Status:", response.status); 
                if (!response.ok) {
                    // If the token is invalid, the backend will return a 401 Unauthorized error
                    if (response.status === 401) {
                         throw new Error(`Authentication failed. Please log in again.`);
                    }
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                console.log("API Data:", data); 
                setDashboardData(data);
            } catch (err) {
                console.error("Error fetching data:", err);
                setError(err.message || "Failed to load content. Please try again.");
            } finally {
                setIsLoading(false);
            }
        };

        // Only fetch data if the user is authenticated.
        if (isAuthenticated) {
            fetchData();
        } else {
            setError("User not authenticated. Please log in.");
            setIsLoading(false);
        }
    }, [activeSidebarItem, isAuthenticated]);

    const handleSidebarItemClick = (itemId) => {
        navigate(itemId === 'dashboard' ? `/venting-room-dashboard` : `/venting-room-dashboard/${itemId}`);
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="flex items-center justify-center h-full">
                    <FaSpinner className="animate-spin text-5xl text-blue-500" />
                </div>
            );
        }
        if (error) {
            return (
                <div className="flex items-center justify-center h-full">
                    <p className="text-red-500 text-xl">{error}</p>
                </div>
            );
        }
        
        switch (activeSidebarItem) {
            case 'dashboard':
                return <DashboardContent data={dashboardData} />;
            case 'usage-report':
                return <UsageReportContent data={dashboardData} />;
            case 'trending-news':
                return <TrendingNewsContent data={dashboardData} />;
            case 'privacy-settings':
                return <PrivacySettingsContent data={dashboardData} />;
            case 'notifications':
                return <NotificationsContent data={dashboardData} />;
            default:
                return <DashboardContent data={dashboardData} />;
        }
    };

    return (
        <div className="dashboard-container">
            <div className="sidebar">
                <h2>Venting Room</h2>
                <ul className="sidebar-list">
                    {[
                        { id: 'dashboard', label: 'Dashboard', icon: <FaHome /> },
                        { id: 'usage-report', label: 'Usage Report', icon: <FaChartLine /> },
                        { id: 'trending-news', label: 'Trending News', icon: <FaTasks /> },
                        { id: 'privacy-settings', label: 'Privacy Settings', icon: <FaCog /> },
                        { id: 'notifications', label: 'Notifications', icon: <FaBell /> },
                    ].map((item) => (
                        <li
                            key={item.id}
                            className={`sidebar-item ${activeSidebarItem === item.id ? 'active' : ''}`}
                            onClick={() => handleSidebarItemClick(item.id)}
                        >
                            {item.icon} {item.label}
                        </li>
                    ))}
                </ul>
            </div>
            <div className="content-area">
                <Routes>
                    <Route index element={renderContent()} />
                    <Route path="usage-report" element={renderContent()} />
                    <Route path="trending-news" element={renderContent()} />
                    <Route path="privacy-settings" element={renderContent()} />
                    <Route path="notifications" element={renderContent()} />
                    <Route path="*" element={<Link to="/dev-venting-room-dashboard">Go to Dashboard</Link>} />
                </Routes>
            </div>
        </div>
    );
}

export default VentingRoomDashboard;